<template>
  <AuthLayout>
    <template #header>
      <Button
        @click="handleBack"
        variant="paper-primary"
        size="sm"
        class=""
        :class="authStore.is_signup ? 'hidden' : 'hidden lg:flex mb-8'"
      >
        <template #icon>
          <Icon name="ChevronLeft" class="h-4 w-4" />
        </template>
      </Button>
      <div v-if="!authStore.is_signup && !authStore.mfa_channel">
        <h1 class="text-[32px] font-semibold">Let's verify it's you</h1>
        <p class="text-strong text-xl -mt-2 font-medium">Choose a verification method</p>
      </div>
      <div v-else>
        <h1 class="text-[32px] font-semibold">Verifying your account</h1>
        <p v-if="authStore.mfa_channel !== 'Skip MFA'" class="text-strongest font-inter">
          A 6 digit verification code has been sent to<br />
          {{
            authStore.is_signup
              ? authStore.signup_fields.email
              : authStore.mfa_channel === "Email"
                ? `${authStore.signin_fields.email[0]}${"*".repeat(authStore.signin_fields.email.split("@")[0].length - 1)}${authStore.signin_fields.email.split("@")[0][authStore.signin_fields.email.split("@")[0].length - 1]}@${authStore.signin_fields.email.split("@")[1]}`
                : `(***)***-${authStore.signin_fields.phone.slice(-4)}`
          }}
        </p>
        <p
          v-else-if="authStore.mfa_channel === 'Skip MFA'"
          class="font-inter text-strongest font-medium"
        >
          MFA bypass: Enter any 6 numbers to skip
        </p>
      </div>
    </template>

    <div class="w-full">
      <!-- MFA Form -->
      <div v-if="!authStore.is_signup && !authStore.mfa_channel" class="flex flex-col gap-2">
        <Button
          v-for="method in mfaMethods"
          :key="method"
          @click="handleRequestVerificationCode(method)"
          :variant="method === 'Skip MFA' ? 'paper-secondary' : 'paper-primary'"
          class="w-full"
        >
          <template v-if="isRequestCodeLoading && selectedMethod === method" #label>
            <IconPulseSpinner class="h-6 w-6 stroke-white" />
          </template>
          <template v-else #label>{{ method }}</template>
        </Button>
        <div v-if="mfa_error" class="mt-3 ml-1 font-inter text-error text-sm flex items-center">
          <Icon
            name="Asterisk"
            class="mr-2 h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-error"
          />
          {{ mfa_error }}
        </div>
      </div>
      <div v-else>
        <div class="flex flex-col mb-4">
          <label class="font-inter text-strongest font-medium mb-4 block">
            Verification Code
          </label>
          <div id="verification-code" @keydown.enter.prevent="handleVerifyCode">
            <OTPInput
              v-slot="{ slots }"
              v-model="verification_code"
              :maxlength="6"
              inputmode="numeric"
              autocomplete="one-time-code"
              :paste-transformer="sanitizePaste"
              auto-focus
              container-class="flex gap-2"
              @complete="onComplete"
            >
              <div
                v-for="(slot, idx) in slots"
                :key="idx"
                class="w-[50px] h-[50px] flex items-center justify-center text-xl rounded-xl text-strongest bg-container-bg font-semibold relative"
                :class="slot.isActive ? 'ring-1 ring-strongest' : ''"
              >
                <span v-if="slot.char !== null">{{ slot.char }}</span>
                <span v-else class="opacity-40">0</span>
                <div v-if="slot.hasFakeCaret" class="otp-caret" />
              </div>
            </OTPInput>
          </div>
          <div v-if="mfa_error" class="mt-3 ml-1 font-inter text-error text-sm flex items-center">
            <Icon
              name="Asterisk"
              class="mr-2 h-[14px] w-[14px] min-w-[14px] -skew-x-12 stroke-error"
            />
            <span class="font-inter">
              {{ mfa_error }} <br />

              <span v-if="authStore.is_signup" class="font-inter">
                If you already have an account,
                <RouterLink to="/sign-in" class="underline font-inter">Sign in</RouterLink>
                instead.
              </span>
            </span>
          </div>
        </div>
        <Button
          @click="handleRequestVerificationCode(authStore.mfa_channel)"
          variant="paper-primary"
          class="min-w-[200px]"
          :disabled="timeUntilNextCodeRequest > 0 || isRequestCodeLoading"
        >
          <template v-if="isRequestCodeLoading" #label>
            <IconPulseSpinner class="h-6 w-6" />
          </template>
          <template v-else #label>{{
            timeUntilNextCodeRequest === 0
              ? "Request Another Code"
              : "Request a new code in (" + timeUntilNextCodeRequest + "s)"
          }}</template>
        </Button>

        <Button
          @click="handleVerifyCode"
          class="w-full mt-8 max-lg:hidden"
          variant="primary-grey"
          :disabled="isVerifyCodeLoading"
        >
          <template v-if="isVerifyCodeLoading" #label>
            <IconPulseSpinner class="h-6 w-6" />
          </template>
          <template v-else #label>{{ button_text }}</template>
        </Button>
      </div>

      <!-- Turnstile widget -->
      <div class="mt-8 mb-6" data-theme="light" id="turnstile"></div>

      <!-- MFA Help Links -->
      <div
        v-if="!authStore.is_signup && !authStore.mfa_channel"
        class="text-cta-tertiary-text text-base font-medium"
      >
        <p class="font-inter">
          Need help?
          <a
            :href="MFAGuide"
            download="MFA_Guide"
            rel="noreferrer noopener"
            class="font-inter underline"
          >
            Multi Factor Authentication Guide
          </a>
        </p>
        <p class="mt-1 font-inter">
          Lost your MFA Method?
          <a
            href="https://phoenixenergy.com/contact"
            rel="noreferrer noopener"
            target="_blank"
            class="font-inter underline"
          >
            Contact Us
          </a>
        </p>
      </div>
    </div>

    <template v-if="authStore.is_signup || authStore.mfa_channel" #cta>
      <Button
        @click="handleVerifyCode"
        class="w-full"
        variant="primary-grey"
        :disabled="isVerifyCodeLoading"
      >
        <template v-if="isVerifyCodeLoading" #label>
          <IconPulseSpinner class="h-6 w-6" />
        </template>
        <template v-else #label>{{ button_text }}</template>
      </Button>
    </template>
  </AuthLayout>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";

import { useTurnstile } from "@/composables/useTurnstile";
import { useOfferingExperiment, clearProductParam } from "@/composables/useOfferingExperiment";
import { useStepOrderExperiment } from "@/composables/useStepOrderExperiment";
import { useAnalytics, buildInvestorProfile } from "@/composables/useAnalytics";
import { useAuthStore, useDataStore, usePurchaseFlowStore } from "@/stores";
import { IconPulseSpinner } from "@/components/icons";
import { Button, Icon } from "@/components";
import { OTPInput } from "vue-input-otp";
import AuthLayout from "./AuthLayout.vue";
import MFAGuide from "@/assets/documents/MFA_guide.pdf";

// Turnstile composable - handles init, render, cleanup automatically
const { resetTurnstile } = useTurnstile();

const router = useRouter();
const route = useRoute();

const { initExperiment } = useOfferingExperiment();
const { initExperiment: initStepOrderExperiment } = useStepOrderExperiment();
const {
  identifyContact,
  setInvestorProfile,
  emailVerificationSent,
  emailVerified,
  loginSucceeded,
  loginFailed,
} = useAnalytics();

const authStore = useAuthStore();
const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();
const timeUntilNextCodeRequest = ref(30);

const selectedMethod = ref("");
const mfa_error = ref("");
const isVerifyCodeLoading = ref(false);
const isRequestCodeLoading = ref(false);
const verification_code = ref("");
const autoSubmitLocked = ref(false);
const pendingAutoSubmit = ref(false);
const showSigninLink = ref(false);
let interval = null;
const qaSkipAuthorized = import.meta.env.VITE_ENV === "QA";
const mfaMethods = computed(() =>
  qaSkipAuthorized
    ? ["Skip MFA", "Email", "Text Message", "Phone Call"]
    : ["Email", "Text Message", "Phone Call"]
);

onMounted(async () => {
  window.scrollTo(0, 0);
  if (!authStore.signin_fields.email && !authStore.signup_fields.email) {
    router.push("/sign-in");
  }

  if (authStore.mfa_channel) {
    timeUntilNextCodeRequest.value = 30;
    interval = setInterval(() => {
      if (timeUntilNextCodeRequest.value > 0) {
        timeUntilNextCodeRequest.value--;
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }
});

watch(
  () => authStore.mfa_channel,
  () => {
    resetTurnstile();
    clearInterval(interval);
    if (authStore.mfa_channel) {
      verification_code.value = "";
      autoSubmitLocked.value = false;
      pendingAutoSubmit.value = false;
      timeUntilNextCodeRequest.value = 30;
      interval = setInterval(() => {
        if (timeUntilNextCodeRequest.value > 0) {
          timeUntilNextCodeRequest.value--;
        } else {
          clearInterval(interval);
        }
      }, 1000);
    }
  }
);

const button_text = computed(() => {
  if (route.fullPath.includes("sign-in")) {
    return "Verify and Sign In";
  } else if (route.fullPath.includes("sign-up")) {
    return "Verify and Create Account";
  } else {
    return "Verify Code";
  }
});

// Strip anything non-digit from typed input (safety net + trims stray whitespace)
watch(verification_code, (v) => {
  const clean = (v || "").replace(/\D/g, "");
  if (clean !== v) verification_code.value = clean;
});

// Strip whitespace/junk from pasted content (passed to OTPInput :paste-transformer)
const sanitizePaste = (pasted) => (pasted || "").replace(/\D/g, "");

function onComplete() {
  // Editing an already-completed code re-fires this event; keep any existing
  // error visible and require an explicit Return/Submit (handled elsewhere).
  if (autoSubmitLocked.value) return;
  autoSubmitLocked.value = true; // lock after first completion
  if (authStore.is_turnstile_valid) {
    handleVerifyCode();
  } else {
    pendingAutoSubmit.value = true; // defer until Turnstile validates
  }
}

// Fire the deferred auto-submit as soon as Turnstile passes
watch(
  () => authStore.is_turnstile_valid,
  (valid) => {
    if (valid && pendingAutoSubmit.value && verification_code.value.length === 6) {
      pendingAutoSubmit.value = false;
      handleVerifyCode();
    }
  }
);

async function handleVerifyCode() {
  if (isVerifyCodeLoading.value) return;
  try {
    mfa_error.value = "";
    pendingAutoSubmit.value = false;
    isVerifyCodeLoading.value = true;
    if (!authStore.is_turnstile_valid) {
      mfa_error.value = "Please complete the security check to confirm that you are a human.";
      return;
    }

    const code = verification_code.value.replace(/\D/g, "");
    const verifyResult = await authStore.verifyCodeAndSignin(code);

    if (verifyResult.session_id) {
      authStore.is_verified = true;
      authStore.active_signup_step = 5;
      await dataStore.setData();
      if (!authStore.is_signup) clearProductParam();
      identifyContact(dataStore.contact);
      setInvestorProfile(
        buildInvestorProfile(
          dataStore.contact,
          dataStore.investments,
          authStore.is_signup ? "sign_up" : "sign_in"
        )
      );
      const mfaMethod = {
        Email: "email",
        "Text Message": "sms",
        "Phone Call": "phone",
        "Skip MFA": "skip",
      };
      const method = mfaMethod[authStore.mfa_channel] ?? "email";
      if (authStore.is_signup) {
        emailVerified({ method });
      } else {
        loginSucceeded({ method });
      }
      initExperiment();
      initStepOrderExperiment();
      authStore.is_first_login = true;
      const hasSignedInvestments = purchaseFlowStore.signedInvestmentStatus.has_signed_investment;
      router.push(hasSignedInvestments ? "/dashboard" : "/make-an-investment");
    } else if (
      verifyResult.error &&
      verifyResult.error === "Unexpected Error: Max check attempts reached"
    ) {
      showSigninLink.value = false;
      mfa_error.value =
        "You have made too many incorrect attempts. Please wait a few minutes before trying again.";
      loginFailed({ reason: "max_attempts" });
      resetTurnstile();
    } else {
      mfa_error.value = "Please check the code you entered is correct, or request a new one.";
      showSigninLink.value = true;
      loginFailed({ reason: "invalid_code" });
      resetTurnstile();
    }
  } catch (err) {
    mfa_error.value = "Please check the code you entered is correct, or request a new one.";
    showSigninLink.value = true;
    resetTurnstile();
  } finally {
    isVerifyCodeLoading.value = false;
  }
}

async function handleRequestVerificationCode(channel) {
  try {
    mfa_error.value = "";
    isRequestCodeLoading.value = true;
    selectedMethod.value = channel;
    let sendCodeResult = { status: null };

    if (!authStore.is_turnstile_valid) {
      mfa_error.value = "Please complete the security check to confirm that you are a human.";
      return;
    } else {
      sendCodeResult = await authStore.sendVerificationCode(channel);
    }

    if (
      sendCodeResult.error &&
      sendCodeResult.error === "Unexpected Error: Max send attempts reached"
    ) {
      resetTurnstile();
      mfa_error.value =
        "You have made too many recent requests. Please wait a few minutes before trying again.";
      return;
    }
    if (sendCodeResult.status && sendCodeResult.status !== "pending") {
      resetTurnstile();
      mfa_error.value = "Failed to send verification code. Please try again.";
      return;
    }

    // Only reset timer and code on success
    authStore.mfa_channel = channel;
    const mfaMethod = {
      Email: "email",
      "Text Message": "sms",
      "Phone Call": "phone",
      "Skip MFA": "skip",
    };
    emailVerificationSent({ method: mfaMethod[channel] ?? "email" });
    clearInterval(interval);
    verification_code.value = "";
    autoSubmitLocked.value = false;
    pendingAutoSubmit.value = false;
    timeUntilNextCodeRequest.value = 30;
    interval = setInterval(() => {
      if (timeUntilNextCodeRequest.value > 0) {
        timeUntilNextCodeRequest.value--;
      } else {
        clearInterval(interval);
      }
    }, 1000);
  } catch (err) {
    mfa_error.value = "Failed to send verification code. Please try again.";
    resetTurnstile();
  } finally {
    isRequestCodeLoading.value = false;
    selectedMethod.value = "";
  }
}

function handleBack() {
  if (authStore.mfa_channel) {
    authStore.mfa_channel = null;
    mfa_error.value = "";
    return;
  }

  router.push("/sign-in");
}
</script>

<style scoped>
.otp-caret {
  position: absolute;
  width: 1px;
  height: 1.75rem;
  background: currentColor;
  animation: otp-caret-blink 1s steps(1) infinite;
}
@keyframes otp-caret-blink {
  0%,
  50% {
    opacity: 1;
  }
  50.01%,
  100% {
    opacity: 0;
  }
}
</style>
